import { NumberGrid } from '@Animation/mechanics/base/number-grid'
import { NumberSelector } from '@Animation/mechanics/base/number-selector'
import { PLINKO_ATLAS_FRAMES, PLINKO_TEXTURE_KEYS } from '.'
import { tweenPromise } from '@Animation/utils'
import { Utils } from '@Util/utils'
import { AnimationActions } from '@AppState/animation'
import { store } from '@AppState/redux-store'

type Location = {
  x: number
  y: number
}

const EVEN_ROW_X_POSITIONS = [11, 107, 201, 296, 393, 486]
const ODD_ROW_X_POSITIONS = [61, 155, 251, 346, 438]
const PEG_Y_POSITIONS = [75, 123, 168, 214, 260, 306, 353, 400, 446]

export class PlinkoNumberSelector extends NumberSelector {
  ball: Phaser.GameObjects.Image
  pegLocations: Location[]

  constructor(
    scene: Phaser.Scene,
    numberGrid: NumberGrid,
    miniGameSelections: number[],
    preNumberSelected: (index: number) => Promise<void>,
    onNumberSelected: (number: number) => void,
  ) {
    super(scene, numberGrid, miniGameSelections, preNumberSelected, onNumberSelected)

    this.ball = scene.add.image(0, 0, PLINKO_TEXTURE_KEYS.ATLAS, PLINKO_ATLAS_FRAMES.BALL)
    this.ball.setOrigin(0.5, 1)
    this.ball.setVisible(false)
    this.ball.setScale(0.35)

    this.pegLocations = []
    for (let row = 0; row < PEG_Y_POSITIONS.length; row++) {
      const evenRow = row % 2 === 0

      const xPositions = evenRow ? EVEN_ROW_X_POSITIONS : ODD_ROW_X_POSITIONS

      for (let column = 0; column < xPositions.length; column++) {
        const xPos = xPositions[column]
        const yPos = PEG_Y_POSITIONS[row]

        this.pegLocations.push({ x: xPos, y: yPos })
      }
    }

    this.add(this.ball)
  }

  getPegRowBeneathTarget(targetLocation: Location) {
    for (let i = 0; i < PEG_Y_POSITIONS.length; i++) {
      if (PEG_Y_POSITIONS[i] > targetLocation.y) {
        return i
      }
    }
  }

  getDistanceBetweenLocations(a: Location, b: Location) {
    return Math.abs(Math.sqrt(Math.pow(a.x - b.x, 2) + Math.pow(a.y - b.y, 2)))
  }

  sortByDistanceFrom(locations: Location[], target: Location): Location[] {
    return locations.sort((a, b) => {
      const distanceA = Math.sqrt(Math.pow(a.x - target.x, 2) + Math.pow(a.y - target.y, 2))
      const distanceB = Math.sqrt(Math.pow(b.x - target.x, 2) + Math.pow(b.y - target.y, 2))
      return distanceA - distanceB
    })
  }

  async targetSingleNumber(number: number): Promise<void> {
    const targetX = this.numberGrid.x + this.numberGrid.numbers[number - 1].x
    const targetY = this.numberGrid.y + this.numberGrid.numbers[number - 1].y

    const path: Location[] = [{ x: targetX, y: targetY }]

    const lastBounceIndex = this.getPegRowBeneathTarget({ x: targetX, y: targetY })

    if (!lastBounceIndex) {
      store.dispatch(
        AnimationActions.crash(`Plinko | Cannot Determine Proper Path to Target ${number}`),
      )
      return
    }

    for (let bounce = lastBounceIndex; bounce >= 0; bounce--) {
      const lastLocation = path[path.length - 1]

      const pegsInRow = this.pegLocations.filter((loc) => loc.y === PEG_Y_POSITIONS[bounce])

      const sortedLocations = this.sortByDistanceFrom(pegsInRow, lastLocation)
      const distances = sortedLocations.map((loc) =>
        this.getDistanceBetweenLocations(loc, lastLocation),
      )

      const filteredLocations = sortedLocations.filter(
        (_loc, index) => distances[index] < distances[0] * 1.5,
      )

      if (filteredLocations.length === 0) {
        throw new Error(`Cannot Determine Proper Path to Target ${number}, bounce ${bounce}`)
      }

      path.push(Utils.getRandomElementFromArray(filteredLocations))
    }

    const startingY = 0
    const startingX = path[path.length - 1].x
    const velocity = 0.2

    this.ball.x = startingX
    this.ball.y = startingY
    this.ball.setVisible(true)
    path.push({ x: startingX, y: startingY })
    path.reverse()

    //Drop Ball
    await tweenPromise(this.scene, {
      targets: this.ball,
      duration: this.getDistanceBetweenLocations(path[0], path[1]) / velocity,
      x: path[1].x,
      y: path[1].y,
    })()

    for (let i = 2; i < path.length; i++) {
      const startingLoc = path[i - 1]
      const nextTarget = path[i]

      const bounceDuration = this.getDistanceBetweenLocations(nextTarget, startingLoc) / velocity

      const bounceHeight = 12
      const isBouncingUp = nextTarget.y < startingLoc.y - bounceHeight

      let bounceY = startingLoc.y - bounceHeight
      if (nextTarget.y < bounceY) bounceY = nextTarget.y

      if (isBouncingUp) {
        //Bounce Straight to destination
        const bounceHorizontal = tweenPromise(this.scene, {
          targets: this.ball,
          duration: bounceDuration,
          x: nextTarget.x,
        })

        const bounceVertical = tweenPromise(this.scene, {
          targets: this.ball,
          duration: bounceDuration,
          y: nextTarget.y,
          ease: Phaser.Math.Easing.Back.Out,
        })

        await Promise.all([bounceHorizontal(), bounceVertical()])
      } else {
        const bounceHorizontal = tweenPromise(this.scene, {
          targets: this.ball,
          duration: bounceDuration,
          x: nextTarget.x,
        })
        const bounceVerticalUp = tweenPromise(this.scene, {
          targets: this.ball,
          duration: bounceDuration / 2,
          y: bounceY,
          ease: Phaser.Math.Easing.Circular.Out,
        })

        const bounceVerticalDown = tweenPromise(this.scene, {
          targets: this.ball,
          duration: bounceDuration / 2,
          y: nextTarget.y,
          ease: Phaser.Math.Easing.Circular.In,
        })

        bounceHorizontal()
        await bounceVerticalUp()
        await bounceVerticalDown()
      }
    }

    this.ball.setVisible(false)
    await this.numberGrid.selectNumber(number)
  }

  async skip(numbers: number[]) {
    this.ball.setVisible(false)
    super.skip(numbers)
  }
}
